import { Chip } from "@dropbox/dig-components/chip";
import type {
  ChannelFilter,
  ChannelForFilter,
} from "@mirage/shared/search/search-filters";
import { SearchFilterType } from "@mirage/shared/search/search-filters";
import { KeyCodes } from "@mirage/shared/util/constants";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChannelFilterTypeahead } from "./ChannelFilterTypeahead";

type ChannelFilterProps = {
  onSelect: (value: ChannelFilter) => void;
  selectedChannels: ChannelForFilter[] | undefined;
  onOpenDropdown: () => void;
};

export const ChannelFilterDropdown = ({
  onSelect,
  selectedChannels,
  onOpenDropdown,
}: ChannelFilterProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const isChannelFilterActive =
    !!selectedChannels && selectedChannels.length > 0;
  const channelFilterChipLabel = isChannelFilterActive
    ? t("num_channels", { count: selectedChannels.length })
    : t("channel_filter_chip_placeholder");
  const handleToggle = () => {
    setIsOpen((prev) => {
      const newIsOpen = !prev;

      if (newIsOpen) {
        onOpenDropdown();
      }
      return newIsOpen;
    });
  };

  const onChannelSelection = (channel: ChannelForFilter) => {
    onSelect({
      id: channel.name,
      type: SearchFilterType.Channel,
      parameters: {
        channelId: channel.uuid,
        channelName: channel.name,
        channelType: channel.type || { ".tag": "channel_type_unset" },
      },
    });
    anchorRef?.current?.focus();
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case KeyCodes.arrowUp:
      case KeyCodes.arrowDown:
        setIsOpen(true);
        return;
    }
  };

  return (
    <div>
      <Chip
        withDropdownIcon={true}
        onClick={handleToggle}
        isSelected={isChannelFilterActive}
        selectedStyle="stroke"
        aria-pressed={isChannelFilterActive}
        onKeyDown={handleTriggerKeyDown}
        ref={anchorRef}
        aria-label={t("filter_bar_channel_dropdown_label")}
      >
        <Chip.Content>{channelFilterChipLabel}</Chip.Content>
      </Chip>
      <ChannelFilterTypeahead
        selectedChannels={selectedChannels || []}
        onChannelSelection={onChannelSelection}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        anchorRef={anchorRef}
      />
    </div>
  );
};
